import type { CallSession } from '@cometchat/calls-sdk-javascript';
import { createContext, useContext } from 'react';
import { composeSessionSettings } from '../utils/session-settings';

/** Types, context and helpers for the ringing flow (see CallProvider). */

export type CallKind = 'audio' | 'video';
export interface CallPeer {
  /** A user's uid, or a group's guid when `kind` is 'group'. */
  uid: string;
  name: string;
  avatar?: string;
  kind?: 'user' | 'group';
}

export type CallPhase =
  | { kind: 'idle' }
  | {
      kind: 'outgoing';
      peer: CallPeer;
      type: CallKind;
      /** 'calling' until the callee's device has the call (onParticipantConnected), then 'ringing'. */
      status: 'calling' | 'ringing' | 'ended';
      /** Set once initiateCall created the call (possibly still 'calling'). */
      sessionId?: string;
      endText?: string;
    }
  | {
      kind: 'incoming';
      peer: CallPeer;
      type: CallKind;
      sessionId: string;
      /** Set when the call is to a group: its name, for the card. */
      groupName?: string;
    };

export interface JoinRequest {
  sessionId: string;
  type: CallKind;
  title: string;
  /** A 1:1 call (not a group call): see callSettings. */
  isPeerCall: boolean;
  /** The caller's prepared session, from initiateCall. Absent for the callee. */
  session?: CallSession;
}

export interface CallsApi {
  phase: CallPhase;
  /** A call is ringing either way, or a meeting/call is live. */
  busy: boolean;
  joinRequest: JoinRequest | null;
  startCall: (peer: CallPeer, type: CallKind) => void;
  cancel: () => void;
  accept: () => void;
  decline: () => void;
  /** Home takes the pending join exactly once, then performs it. */
  takeJoinRequest: () => JoinRequest | null;
}

/**
 * Settings for a ringing call: the meeting defaults plus voice or video.
 *
 * `isPeerCall` for a 1:1 call, as Android and iOS set it: leaving a peer call ends it for both
 * sides. Without it a web decline (or hang-up) only dropped this tab from the room, and the
 * other side rang on until its own timer — the slow decline QA saw (ENG-40329).
 */
export function callSettings(type: CallKind, title: string, isPeerCall: boolean) {
  return composeSessionSettings({
    title,
    sessionType: type === 'audio' ? 'VOICE' : 'VIDEO',
    isPeerCall,
  });
}

export const CallsContext = createContext<CallsApi | null>(null);

export function useCalls(): CallsApi {
  const api = useContext(CallsContext);
  if (!api) throw new Error('useCalls must be used inside <CallProvider>');
  return api;
}

